import { useState } from 'react';
import { PageHeader } from '../components/Layout';
import {
  Badge,
  Button,
  Card,
  EmptyState,
  ErrorNote,
  Field,
  Input,
  Loading,
  Modal,
  Select,
  StatusBadge,
  Textarea,
} from '../components/ui';
import { api, type Campaign, type LinkedInPost, type Persona, type PostIdea } from '../lib/api';
import { date } from '../lib/format';
import { useAction, useApi } from '../lib/hooks';

/**
 * LinkedIn is assisted-manual: Claude drafts, you publish on LinkedIn, and the
 * app tracks timing and engagement. LinkedIn's API exposes no messaging or
 * profile-view data to standard developer accounts.
 */
export function Posts() {
  const posts = useApi<{ posts: LinkedInPost[] }>('/api/posts');
  const campaigns = useApi<{ campaigns: Campaign[] }>('/api/campaigns');
  const [showIdeas, setShowIdeas] = useState(false);
  const [editing, setEditing] = useState<LinkedInPost | 'new' | null>(null);
  const [seedContent, setSeedContent] = useState('');

  const items = posts.data?.posts ?? [];

  return (
    <>
      <PageHeader
        title="LinkedIn"
        description="Draft posts with Claude, publish on LinkedIn, track what they pull in."
        helpHref="posts"
        actions={
          <>
            <Button onClick={() => setShowIdeas(true)}>Get post ideas</Button>
            <Button
              variant="primary"
              onClick={() => {
                setSeedContent('');
                setEditing('new');
              }}
            >
              New post
            </Button>
          </>
        }
      />

      <div className="mb-4 rounded-md bg-amber-50 px-3 py-2.5 text-sm text-amber-900 ring-1 ring-inset ring-amber-200">
        LinkedIn's API does not offer DM sending or profile-view data to standard developer accounts,
        and scraping breaches their terms. So this section is assisted-manual: the app writes and
        tracks, you publish and log the engagement.
      </div>

      {posts.loading && <Loading />}
      {posts.error && <ErrorNote message={posts.error} onRetry={posts.reload} />}

      {!posts.loading && items.length === 0 && (
        <EmptyState
          title="No posts yet"
          description="Ask Claude for ideas grounded in the objections your prospects actually raised."
          action={<Button variant="primary" onClick={() => setShowIdeas(true)}>Get post ideas</Button>}
        />
      )}

      <div className="grid gap-3 md:grid-cols-2">
        {items.map((post) => (
          <Card
            key={post.id}
            title={
              <span className="flex items-center gap-2">
                <StatusBadge status={post.status} />
                {post.campaign_name && <Badge tone="blue">{post.campaign_name}</Badge>}
              </span>
            }
            action={
              <Button size="sm" onClick={() => setEditing(post)}>
                Edit
              </Button>
            }
          >
            <p className="whitespace-pre-wrap text-sm text-ink-800">{post.content}</p>
            <div className="mt-3 flex flex-wrap items-center gap-3 border-t border-ink-100 pt-3 text-xs text-ink-500">
              <span>{post.likes} likes</span>
              <span>{post.comments} comments</span>
              <span>{post.profile_views} profile views</span>
              {(post.engagement_count ?? 0) > 0 && (
                <Badge
                  tone="green"
                  title="Prospects you logged as having liked or commented on this post — a warm-up touch before a direct message."
                >
                  {post.engagement_count} prospects engaged
                </Badge>
              )}
              <span className="ml-auto">
                {post.posted_at ? date(post.posted_at) : post.scheduled_for ? `scheduled ${date(post.scheduled_for)}` : 'idea'}
              </span>
            </div>
            {post.post_url && (
              <a
                href={post.post_url}
                target="_blank"
                rel="noreferrer"
                className="mt-2 inline-block text-xs font-medium text-accent-600 hover:underline"
              >
                View on LinkedIn
              </a>
            )}
          </Card>
        ))}
      </div>

      <IdeasModal
        open={showIdeas}
        onClose={() => setShowIdeas(false)}
        campaigns={campaigns.data?.campaigns ?? []}
        onUse={(draft) => {
          setShowIdeas(false);
          setSeedContent(draft);
          setEditing('new');
        }}
      />

      <PostModal
        open={editing !== null}
        post={editing === 'new' ? null : editing}
        seedContent={seedContent}
        campaigns={campaigns.data?.campaigns ?? []}
        onClose={() => setEditing(null)}
        onDone={() => {
          setEditing(null);
          posts.reload();
        }}
      />
    </>
  );
}

function IdeasModal({
  open,
  onClose,
  campaigns,
  onUse,
}: {
  open: boolean;
  onClose: () => void;
  campaigns: Campaign[];
  onUse: (draft: string) => void;
}) {
  const [campaignId, setCampaignId] = useState('');
  const [personaId, setPersonaId] = useState('');
  const [ideas, setIdeas] = useState<PostIdea[]>([]);

  const personas = useApi<{ personas: Persona[] }>(
    campaignId ? `/api/personas?campaign_id=${campaignId}` : '/api/personas',
  );

  const generate = useAction(async () => {
    const response = await api.post<{ ideas: PostIdea[] }>('/api/posts/ideas', {
      campaign_id: campaignId || undefined,
      persona_id: personaId || undefined,
      count: 3,
    });
    setIdeas(response.ideas);
  });

  return (
    <Modal open={open} onClose={onClose} title="Post ideas from Claude" width="max-w-2xl">
      <div className="space-y-3">
        <p className="text-sm text-ink-600">
          Ideas are grounded in the pain points and objections prospects raised in the last 90 days,
          plus the voice of your published posts.
        </p>

        <div className="grid gap-3 sm:grid-cols-2">
          <Field label="Campaign">
            <Select value={campaignId} onChange={(event) => setCampaignId(event.target.value)}>
              <option value="">Any campaign</option>
              {campaigns.map((campaign) => (
                <option key={campaign.id} value={campaign.id}>
                  {campaign.name}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="Persona">
            <Select value={personaId} onChange={(event) => setPersonaId(event.target.value)}>
              <option value="">Any persona</option>
              {personas.data?.personas.map((persona) => (
                <option key={persona.id} value={persona.id}>
                  {persona.name}
                </option>
              ))}
            </Select>
          </Field>
        </div>

        <Button variant="primary" loading={generate.pending} onClick={() => void generate.run()}>
          Generate 3 ideas
        </Button>

        {generate.error && <ErrorNote message={generate.error} />}

        <div className="space-y-3">
          {ideas.map((idea, index) => (
            <div key={index} className="rounded-md border border-ink-200 p-3">
              <p className="text-sm font-semibold text-ink-900">{idea.hook}</p>
              <p className="mt-0.5 text-xs text-ink-500">{idea.angle}</p>
              <p className="mt-2 whitespace-pre-wrap text-sm text-ink-700">{idea.draft}</p>
              <Button size="sm" className="mt-2" onClick={() => onUse(idea.draft)}>
                Use this draft
              </Button>
            </div>
          ))}
        </div>
      </div>
    </Modal>
  );
}

function PostModal({
  open,
  post,
  seedContent,
  campaigns,
  onClose,
  onDone,
}: {
  open: boolean;
  post: LinkedInPost | null;
  seedContent: string;
  campaigns: Campaign[];
  onClose: () => void;
  onDone: () => void;
}) {
  const [content, setContent] = useState(post?.content ?? seedContent);
  const [status, setStatus] = useState(post?.status ?? 'idea');
  const [campaignId, setCampaignId] = useState(post?.campaign_id ?? '');
  const [postUrl, setPostUrl] = useState(post?.post_url ?? '');
  const [likes, setLikes] = useState(String(post?.likes ?? 0));
  const [comments, setComments] = useState(String(post?.comments ?? 0));
  const [profileViews, setProfileViews] = useState(String(post?.profile_views ?? 0));

  // Re-seed when a different post (or a new one) is opened.
  const [seededFor, setSeededFor] = useState(post?.id ?? seedContent);
  if (open && (post?.id ?? seedContent) !== seededFor) {
    setSeededFor(post?.id ?? seedContent);
    setContent(post?.content ?? seedContent);
    setStatus(post?.status ?? 'idea');
    setCampaignId(post?.campaign_id ?? '');
    setPostUrl(post?.post_url ?? '');
    setLikes(String(post?.likes ?? 0));
    setComments(String(post?.comments ?? 0));
    setProfileViews(String(post?.profile_views ?? 0));
  }

  const save = useAction(async () => {
    const payload = {
      content,
      status,
      campaign_id: campaignId || null,
      post_url: postUrl || null,
      likes: Number(likes),
      comments: Number(comments),
      profile_views: Number(profileViews),
    };
    if (post) await api.put(`/api/posts/${post.id}`, payload);
    else await api.post('/api/posts', payload);
    onDone();
  });

  const remove = useAction(async () => {
    if (post) await api.del(`/api/posts/${post.id}`);
    onDone();
  });

  return (
    <Modal open={open} onClose={onClose} title={post ? 'Edit post' : 'New post'} width="max-w-2xl">
      <div className="space-y-3">
        <Field label="Post content">
          <Textarea rows={8} value={content} onChange={(event) => setContent(event.target.value)} />
        </Field>

        <div className="grid gap-3 sm:grid-cols-2">
          <Field label="Status">
            <Select value={status} onChange={(event) => setStatus(event.target.value as typeof status)}>
              <option value="idea">Idea</option>
              <option value="scheduled">Scheduled</option>
              <option value="published">Published</option>
            </Select>
          </Field>
          <Field label="Campaign">
            <Select value={campaignId} onChange={(event) => setCampaignId(event.target.value)}>
              <option value="">No campaign</option>
              {campaigns.map((campaign) => (
                <option key={campaign.id} value={campaign.id}>
                  {campaign.name}
                </option>
              ))}
            </Select>
          </Field>
        </div>

        <Field label="LinkedIn URL" hint="Paste it after publishing, so engagement can be tracked back.">
          <Input value={postUrl} onChange={(event) => setPostUrl(event.target.value)} />
        </Field>

        <div className="grid grid-cols-3 gap-3">
          <Field label="Likes">
            <Input type="number" value={likes} onChange={(event) => setLikes(event.target.value)} />
          </Field>
          <Field label="Comments">
            <Input type="number" value={comments} onChange={(event) => setComments(event.target.value)} />
          </Field>
          <Field label="Profile views">
            <Input
              type="number"
              value={profileViews}
              onChange={(event) => setProfileViews(event.target.value)}
            />
          </Field>
        </div>

        {(save.error || remove.error) && <ErrorNote message={(save.error ?? remove.error) as string} />}

        <div className="flex justify-between gap-2 pt-1">
          {post ? (
            <Button variant="danger" loading={remove.pending} onClick={() => void remove.run()}>
              Delete
            </Button>
          ) : (
            <span />
          )}
          <div className="flex gap-2">
            <Button onClick={onClose}>Cancel</Button>
            <Button
              variant="primary"
              loading={save.pending}
              disabled={!content.trim()}
              onClick={() => void save.run()}
            >
              Save post
            </Button>
          </div>
        </div>
      </div>
    </Modal>
  );
}
